import { usePasswordLoginMutation } from '../apis/loginApi';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { AlertType, TowerAlert } from '../../../common/Alerts';
import { useNextQueryStringValue } from './useQueryString';
import { SessionType } from '../types';
import useRedirectOrCloseOnSuccess from './useRedirectOrCloseOnSuccess';

type Response = {
    onPasswordLogin: (u: string, p: string, isPublic: boolean, mfaCode: string) => void;
    isMfaRequired: boolean;
    isLoading: boolean;
    alerts: TowerAlert[];
    resetAlerts: () => void;
    reset: () => void;
};
export function usePasswordLogin(): Response {
    const [isProcessingLogin, setIsProcessingLogin] = useState(false);
    const [isMfaRequired, setIsMfaRequired] = useState(false);
    const [
        passwordLogin,
        { data: loginResult, isSuccess, isError, error, reset: resetPasswordLogin },
    ] = usePasswordLoginMutation();
    const [alerts, setAlerts] = useState<TowerAlert[]>([]);
    const next = useNextQueryStringValue();

    // Redirect or close the window on successful login (prevent redirect if is MFA and has not verified)
    useRedirectOrCloseOnSuccess(loginResult?.redirectUrl as string);

    useEffect(() => {
        if (isSuccess) {
            // If a password login is attempted for MFA enrolled users and no code is provided, the endpoint will
            // send an MFA contact code to the user's email and response with a isMfaRequired=true and redirectUrl of null.
            setIsProcessingLogin(!isEmpty(loginResult?.redirectUrl)); // This will continue to show login button as disabled during successful redirects.
            setIsMfaRequired(!!loginResult?.isMfaRequired);
        }
    }, [isSuccess, loginResult]);

    useEffect(() => {
        if (isError) {
            setIsProcessingLogin(false);
            const defaultErrorMessage = 'Error processing your login.';
            let errorMessage = defaultErrorMessage;

            if (error) {
                if (typeof error === 'string') {
                    errorMessage = error;
                } else if (error && typeof error === 'object' && 'data' in error) {
                    const errorData = (error as any).data;
                    errorMessage = errorData?.message || defaultErrorMessage;
                }
            }

            setAlerts([
                {
                    type: AlertType.error,
                    message: errorMessage,
                },
            ]);
        }
    }, [isError, error]);

    const onPasswordLogin = (
        username: string,
        password: string,
        isPublic: boolean,
        mfaCode?: string
    ) => {
        if (isProcessingLogin) {
            return;
        }
        if (isEmpty(username) || isEmpty(password)) {
            setAlerts([
                {
                    type: AlertType.error,
                    message: 'Missing required username or password to login.',
                },
            ]);
            return;
        }

        setAlerts([]);
        setIsProcessingLogin(true);

        const sessionType = isPublic ? SessionType.PUBLIC : SessionType.PRIVATE;
        passwordLogin({ username, password, sessionType, next, mfaCode });
    };

    const reset = () => {
        setAlerts([]);
        setIsProcessingLogin(false);
        setIsMfaRequired(false);
        resetPasswordLogin();
    };

    return {
        onPasswordLogin,
        isMfaRequired,
        isLoading: isProcessingLogin,
        alerts,
        resetAlerts: () => setAlerts([]),
        reset,
    };
}
